import { useState, useEffect, useRef } from 'react';
import { ConfirmDialog, ConfirmDialogSimple } from '@/components/ui/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { CalendarNote, CreateNoteData, UpdateNoteData, RepeatType } from '@/types/calendar-notes';
import { CancelButton } from '@/components/ui/CancelButton';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { Icon } from '@/components/ui/Icon';
import { Select } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { DayPicker, DateRange } from 'react-day-picker';
import { format, isSameDay } from 'date-fns';
import { getDateLocale } from '@/utils/dateFormat';
import 'react-day-picker/style.css';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import * as Popover from '@radix-ui/react-popover';
import { EmojiClickData } from 'emoji-picker-react';
import { EmojiPickerCompact } from '@/components/ui/EmojiPickerCompact';
import { searchEmojis } from '@/utils/emojiAutocomplete';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { getStackZIndex } from '@/contexts/ModalStackContext';

/**
 * Campaign Modal Component
 *
 * Modal for creating/editing calendar campaigns (CalendarNote).
 * Clean Hobonichi-style design with campaign terminology.
 */

interface CampaignModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Close handler
   */
  onClose: () => void;

  /**
   * Calendar ID to create note for
   */
  calendarId: number;

  /**
   * Initial date for new campaign
   */
  initialDate?: Date;

  /**
   * Existing campaign to edit (if editing)
   */
  campaign?: CalendarNote;

  /**
   * Submit handler
   */
  onSubmit: (data: CreateNoteData | { id: number; data: UpdateNoteData }) => void;

  /**
   * Whether the mutation is in progress
   */
  isLoading?: boolean;

  /**
   * Whether to show repetition options (default: true)
   * Set to false for simple campaigns, true for content pillars
   */
  showRepetition?: boolean;

  /**
   * Custom title for the modal
   */
  modalTitle?: string;

  /**
   * Delete handler (only for editing)
   */
  onDelete?: (campaignId: number) => void;
}

export const CampaignModal = ({
  isOpen,
  onClose,
  calendarId,
  initialDate,
  campaign,
  onSubmit,
  isLoading = false,
  showRepetition = true,
  modalTitle,
  onDelete,
}: CampaignModalProps) => {
  const { t } = useTranslation('common');
  const isEditing = !!campaign;

  // Default campaign color (blue from the ColorPicker palette)
  const DEFAULT_COLOR = '#3B82F6';

  /**
   * Repeat options (moved inside component for i18n)
   */
  const REPEAT_OPTIONS: Array<{ label: string; value: RepeatType | 'NONE' }> = [
    { label: t('repeat_none'), value: 'NONE' },
    { label: t('repeat_daily'), value: 'DAILY' },
    { label: t('repeat_weekly'), value: 'WEEKLY' },
    { label: t('repeat_monthly'), value: 'MONTHLY' },
    { label: t('repeat_yearly'), value: 'YEARLY' },
  ];

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [repeatType, setRepeatType] = useState<RepeatType | 'NONE'>('NONE');

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Emoji picker state
  const [emojiOpen, setEmojiOpen] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Emoji autocomplete state
  const [emojiQuery, setEmojiQuery] = useState('');
  const [emojiSuggestions, setEmojiSuggestions] = useState<Array<{ shortcode: string; native: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);

  // Scroll selected suggestion into view when navigating with keyboard
  useEffect(() => {
    if (!showSuggestions || !suggestionsRef.current) return;
    const selected = suggestionsRef.current.children[selectedSuggestionIndex] as HTMLElement;
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedSuggestionIndex, showSuggestions]);

  // Search for emoji suggestions when query changes
  useEffect(() => {
    if (emojiQuery.length >= 2) {
      const matches = searchEmojis(emojiQuery);
      setEmojiSuggestions(matches);
      setShowSuggestions(matches.length > 0);
      setSelectedSuggestionIndex(0);
    } else {
      setEmojiSuggestions([]);
      setShowSuggestions(false);
    }
  }, [emojiQuery]);

  // Detect :shortcode pattern as user types in the title
  const handleTitleChange = (value: string) => {
    setTitle(value);
    const input = titleInputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart ?? value.length;
    const textBeforeCursor = value.slice(0, cursorPos);
    const match = textBeforeCursor.match(/:([a-zA-Z0-9_+-]{2,})$/);
    if (match) {
      setEmojiQuery(match[1]);
    } else {
      setEmojiQuery('');
      setShowSuggestions(false);
    }
  };

  // Insert selected emoji from autocomplete suggestions
  const insertEmojiSuggestion = (emoji: { shortcode: string; native: string }) => {
    const input = titleInputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart ?? title.length;
    const textBeforeCursor = title.slice(0, cursorPos);
    const textAfterCursor = title.slice(cursorPos);
    const colonIndex = textBeforeCursor.lastIndexOf(':');
    if (colonIndex === -1) return;

    const newTitle = textBeforeCursor.slice(0, colonIndex) + emoji.native + textAfterCursor;
    setTitle(newTitle);
    setShowSuggestions(false);
    setEmojiQuery('');

    setTimeout(() => {
      const newPos = colonIndex + emoji.native.length;
      input.selectionStart = input.selectionEnd = newPos;
      input.focus();
    }, 0);
  };

  // Keyboard navigation for autocomplete suggestions
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || emojiSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < emojiSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev > 0 ? prev - 1 : emojiSuggestions.length - 1
      );
    } else if (e.key === 'Enter' && showSuggestions) {
      e.preventDefault();
      insertEmojiSuggestion(emojiSuggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      insertEmojiSuggestion(emojiSuggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      // Consume the key: closing the suggestion list must not also close the modal.
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  // Insert emoji at cursor position in the title input
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const input = titleInputRef.current;
    const emoji = emojiData.emoji;

    if (input) {
      const start = input.selectionStart ?? title.length;
      const end = input.selectionEnd ?? title.length;
      const newTitle = title.slice(0, start) + emoji + title.slice(end);
      setTitle(newTitle);

      // Restore focus and move cursor after the inserted emoji
      requestAnimationFrame(() => {
        input.focus();
        const newCursor = start + emoji.length;
        input.setSelectionRange(newCursor, newCursor);
      });
    } else {
      setTitle((prev) => prev + emoji);
    }

    setEmojiOpen(false);
  };

  // Initialize form with campaign data or defaults
  useEffect(() => {
    if (isOpen) {
      setPeriodOpen(false);
      if (campaign) {
        // Editing existing campaign - use full ISO string
        setTitle(campaign.title);
        setDescription(campaign.description || '');
        setColor(campaign.color || DEFAULT_COLOR);
        setDateRange({
          from: campaign.startAt ? new Date(campaign.startAt) : undefined,
          to: campaign.endAt ? new Date(campaign.endAt) : undefined,
        });
        setRepeatType(campaign.repeatType || 'NONE');
      } else {
        // Creating new campaign - use full ISO string
        setTitle('');
        setDescription('');
        setColor(DEFAULT_COLOR);
        const dateAtNoon = initialDate ? new Date(initialDate) : new Date();
        dateAtNoon.setHours(12, 0, 0, 0);
        setDateRange({ from: dateAtNoon, to: undefined });
        setRepeatType('NONE');
      }
    }
  }, [isOpen, campaign, initialDate]);

  // Handle form submission
  // Notes are whole-day based: both ends are normalised to local noon (same convention as the
  // create default), so the picker's local-midnight days never collide on the backend's
  // day check. A non-repeating single day or no end is sent as no end (null on update); repeating
  // pillars keep an explicit end (see below).
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const atNoon = (d: Date): Date => {
      const n = new Date(d);
      n.setHours(12, 0, 0, 0);
      return n;
    };
    const from = dateRange?.from;
    const to = dateRange?.to;
    const startAt = from ? atNoon(from).toISOString() : '';
    const isRepeating = repeatType !== 'NONE';
    let endDay: string | null;
    if (from && to && !isSameDay(from, to)) {
      endDay = atNoon(to).toISOString();
    } else if (isRepeating && from && (to || (isEditing && campaign?.endAt))) {
      // Repeating pillar narrowed to one day: null would mean "repeats forever" in the matcher,
      // so keep an explicit end on that day.
      endDay = atNoon(from).toISOString();
    } else {
      // Non-repeating single day, or a repeating pillar that never had an end: no end.
      endDay = null;
    }

    if (isEditing && campaign) {
      // Update existing campaign (null clears the end)
      const updateData: UpdateNoteData = {
        title,
        description: description || undefined,
        color,
        startAt,
        endAt: endDay,
        repeatType: repeatType === 'NONE' ? null : repeatType,
      };
      onSubmit({ id: campaign.id, data: updateData });
    } else {
      // Create new campaign
      const createData: CreateNoteData = {
        calendarId,
        title,
        description: description || undefined,
        color,
        startAt,
        endAt: endDay ?? undefined,
        repeatType: repeatType === 'NONE' ? undefined : repeatType,
      };
      onSubmit(createData);
    }
  };

  // Unsaved-work guard: dirty while open and the form diverges from the
  // loaded campaign (edit) or has any content (create).
  const isDirty = isEditing && campaign
    ? title !== campaign.title ||
      description !== (campaign.description || '') ||
      color !== (campaign.color || DEFAULT_COLOR) ||
      (repeatType || 'NONE') !== (campaign.repeatType || 'NONE') ||
      (dateRange?.from?.getTime() ?? null) !== (campaign.startAt ? new Date(campaign.startAt).getTime() : null) ||
      (dateRange?.to?.getTime() ?? null) !== (campaign.endAt ? new Date(campaign.endAt).getTime() : null)
    : title.trim().length > 0 || description.trim().length > 0;
  useUnsavedGuard(isOpen && isDirty, 'campaign-modal');

  // Close request (Esc, backdrop, Cancel): a dirty form asks before discarding, a clean one closes directly.
  const handleClose = () => {
    if (isLoading) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  // Proper layer in the modal stack: Esc closes only when topmost (not while saving, and not while a
  // nested popover / confirm layer above it is open), and the backdrop/content z-index come from the stack.
  const stackDepth = useEscapeLayer(isOpen, handleClose);
  const stackZ = getStackZIndex(stackDepth);
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        style={{ zIndex: stackZ.backdrop }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 overflow-y-auto"
        style={{ zIndex: stackZ.content }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-modal-title"
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className={cn(
              'relative bg-white rounded-lg shadow-xl',
              'w-full max-w-md',
              'transform transition-all',
              'border border-gray-200'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 id="campaign-modal-title" className="text-lg font-semibold text-gray-900">
                {modalTitle || (isEditing
                  ? campaign?.repeatType ? t('calendar:edit_content_pillar') : t('calendar:edit_calendar_item')
                  : t('create_campaign'))}
              </h2>
              {isEditing && onDelete && (
                <Tooltip content={t('delete', { defaultValue: 'Delete' })}>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isLoading}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={t('delete')}
                  >
                    <Icon name="recycle-bin" size="sm" ariaHidden />
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {/* Name field - label changes based on type */}
              <div>
                <label htmlFor="campaign-title" className="block text-sm font-medium text-gray-700 mb-1">
                  {showRepetition
                    ? t('content_pillar_name_required', { defaultValue: 'Content Pillar Name *' })
                    : t('campaign_name_required')}
                </label>
                <div className="relative">
                  <input
                    id="campaign-title"
                    ref={titleInputRef}
                    type="text"
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    required
                    disabled={isLoading}
                    className={cn(
                      'w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                      'disabled:bg-gray-50 disabled:cursor-not-allowed',
                      'text-sm'
                    )}
                    placeholder={showRepetition
                      ? t('enter_content_pillar_name', { defaultValue: 'Enter content pillar name' })
                      : t('enter_campaign_name')}
                  />

                  {/* Emoji autocomplete suggestions */}
                  {showSuggestions && emojiSuggestions.length > 0 && (
                    <div ref={suggestionsRef} className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto min-w-[200px]">
                      {emojiSuggestions.map((emoji, index) => (
                        <button
                          key={emoji.shortcode}
                          type="button"
                          onClick={() => insertEmojiSuggestion(emoji)}
                          className={cn(
                            'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                            index === selectedSuggestionIndex && 'bg-primary-50'
                          )}
                        >
                          <span className="text-lg">{emoji.native}</span>
                          <span className="text-sm text-gray-600">:{emoji.shortcode}:</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Emoji picker trigger */}
                  <Popover.Root open={emojiOpen} onOpenChange={setEmojiOpen}>
                    <Popover.Trigger asChild>
                      <button
                        type="button"
                        disabled={isLoading}
                        className={cn(
                          'absolute right-1.5 top-1/2 -translate-y-1/2',
                          'h-7 w-7 flex items-center justify-center rounded',
                          'text-gray-400 hover:text-gray-600 transition-colors',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                        aria-label="Insert emoji"
                      >
                        <Icon name={ICONS.EMO_SMILE} size="sm" ariaHidden />
                      </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        className="shadow-xl rounded-lg overflow-hidden bg-white z-[100000]"
                        side="bottom"
                        align="end"
                        sideOffset={6}
                        avoidCollisions={true}
                        collisionPadding={16}
                      >
                        <EmojiPickerCompact
                          onEmojiClick={handleEmojiClick}
                        />
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="campaign-description" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('description')}
                </label>
                <textarea
                  id="campaign-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isLoading}
                  rows={3}
                  className={cn(
                    'w-full px-3 py-2 border border-gray-300 rounded-lg',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                    'disabled:bg-gray-50 disabled:cursor-not-allowed',
                    'text-sm resize-none'
                  )}
                  placeholder={showRepetition
                    ? t('add_content_pillar_details', { defaultValue: 'Add content pillar details' })
                    : t('add_campaign_details')}
                />
              </div>

              {/* Color Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('color')}
                </label>
                <ColorPicker
                  value={color}
                  onChange={setColor}
                  disabled={isLoading}
                />
              </div>

              {/* Date Range - popover calendar picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('period')}
                </label>
                <Popover.Root open={periodOpen} onOpenChange={setPeriodOpen}>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      disabled={isLoading}
                      className={cn(
                        'w-full px-3 py-2 text-sm text-left rounded-lg border bg-white',
                        'transition-colors duration-150 flex items-center justify-between',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        'disabled:bg-gray-50 disabled:cursor-not-allowed',
                        dateRange?.from ? 'text-gray-900 border-gray-300' : 'text-gray-400 border-gray-300'
                      )}
                    >
                      <span>
                        {dateRange?.from
                          ? `${format(dateRange.from, 'dd.MM.yyyy')}${dateRange.to ? ` – ${format(dateRange.to, 'dd.MM.yyyy')}` : ''}`
                          : t('select_period', { defaultValue: 'Select period' })}
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      side="top"
                      align="start"
                      sideOffset={5}
                      collisionPadding={12}
                      className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 date-picker-compact"
                      style={{ zIndex: 100000 }}
                    >
                      <DayPicker
                        mode="range"
                        min={1}
                        required
                        selected={dateRange}
                        onSelect={(range) => {
                          setDateRange(range);
                          if (range?.from && range.to && !isSameDay(range.from, range.to)) {
                            setPeriodOpen(false);
                          }
                        }}
                        defaultMonth={dateRange?.from || undefined}
                        captionLayout="label"
                        numberOfMonths={1}
                        weekStartsOn={1}
                        showWeekNumber
                        locale={getDateLocale()}
                      />
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              </div>

              {/* Repeat - only show if showRepetition is true */}
              {showRepetition && (
                <div>
                  <label htmlFor="campaign-repeat" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                    {t('repeat')}
                    <Tooltip content={t('repeat_tooltip')}>
                      <span className="text-gray-400 hover:text-gray-600 cursor-help">
                        <Icon name={ICONS.INFO} size="sm" />
                      </span>
                    </Tooltip>
                  </label>
                  <Select
                    value={repeatType}
                    onValueChange={(value) => setRepeatType(value as RepeatType | 'NONE')}
                    options={REPEAT_OPTIONS}
                    disabled={isLoading}
                  />
                </div>
              )}

              {/* Footer */}
              <ModalFooter flush>
                  <CancelButton onClick={handleClose} disabled={isLoading} />
                  <button
                    type="submit"
                    disabled={isLoading || !title.trim() || !dateRange?.from}
                    className={cn(
                      'px-4 h-[44px] sm:h-10 text-sm font-medium',
                      'text-primary-600-contrast bg-primary-600',
                      'rounded-lg',
                      'hover:bg-primary-700 transition-colors',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'flex items-center gap-2'
                    )}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>{isEditing ? t('updating') : t('creating')}</span>
                      </>
                    ) : (
                      <>
                        <Icon name={isEditing ? ICONS.SAVE : ICONS.PLUS} size="sm" />
                        <span>
                          {isEditing
                            ? (campaign?.repeatType
                                ? t('update_content_pillar', { defaultValue: 'Update Content Pillar' })
                                : t('update_calendar_item', { defaultValue: 'Update calendar item' }))
                            : (modalTitle || t('create_campaign'))}
                        </span>
                      </>
                    )}
                  </button>
              </ModalFooter>
            </form>
          </div>
        </div>
      </div>
      {/* Discard Confirmation (dirty form closed via Esc / backdrop / Cancel) */}
      <ConfirmDialog
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title={t('discard_changes', { defaultValue: 'Discard Changes?' })}
        description={t('unsaved_changes_lost', { defaultValue: 'Your unsaved changes will be lost.' })}
        variant="danger"
        confirmText={t('discard', { defaultValue: 'Discard' })}
        cancelText={t('continue', { defaultValue: 'Continue Editing' })}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
        closeOnConfirm={false}
      />

      {/* Delete Confirmation */}
      {isEditing && campaign && onDelete && (
        <ConfirmDialogSimple
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          onConfirm={() => {
            onDelete(campaign.id);
            setShowDeleteConfirm(false);
            onClose();
          }}
          title={campaign.repeatType
            ? t('delete_content_pillar', { defaultValue: 'Delete Content Pillar' })
            : t('delete_campaign', { defaultValue: 'Delete Campaign' })}
          description={campaign.repeatType
            ? t('delete_content_pillar_confirm', {
                name: campaign.title,
                defaultValue: `Are you sure you want to delete content pillar "${campaign.title}"? This will remove it from all recurring dates. This action cannot be undone.`,
              })
            : t('delete_campaign_confirm', {
                name: campaign.title,
                defaultValue: `Are you sure you want to delete campaign "${campaign.title}"? This action cannot be undone.`,
              })}
          confirmText={t('delete')}
          variant="danger"
        />
      )}
    </>
  );
};
